/**
 * Where the platform's cut is set.
 *
 * Three views rather than one table, because the money works differently in each and an
 * admin who conflates them mis-prices a whole category:
 *
 *   Products  the fee is ADDED     vendor lists ₹500 → shopper pays ₹550, vendor keeps ₹500
 *   Services  the fee is DEDUCTED  customer pays ₹500 → vendor takes home ₹450
 *   Adoption  neither              a flat charge to an ADOPTER; no vendor, no payout
 *
 * Every row carries a worked ₹500 example for that reason, and the direction is stated
 * above the rows rather than left to be inferred from a percentage.
 *
 * The rest of the console is built on DataTable; this screen deliberately is not. There
 * are twenty-one rules, the set never grows, and every row is an editable control — paging,
 * sorting and column preferences would all be furniture over a form. What it does take from
 * the blueprint is everything else: the layout, the tabs, the tiles, the panels and the
 * tone vocabulary.
 *
 * What is new is consequence. A rule used to be a number in a box; now each row says how
 * many live listings it governs, how many override it, and when it last moved — and a
 * category with NO rule is rendered as what it actually is, which is the vendor keeping
 * the entire sale, rather than as a neutral empty field.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Boxes,
  Heart,
  IndianRupee,
  Loader2,
  Pencil,
  Percent,
  Save,
  Store,
  Wrench,
  X,
} from 'lucide-react';
import { enum_param, use_url_state } from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DetailPanel, Field, FieldList, PanelSkeleton } from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { Label } from '@/components/ui/label';
import { notify } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { date_short, date_time, humanize } from '@/lib/format';
import {
  use_adoption_fee,
  use_commission_coverage,
  use_set_adoption_fee,
  use_set_commission_rule,
  use_set_service_commission_rule,
} from '@/features/commission/hooks';
import {
  FEE_TONE_CLASS,
  VERTICAL_LABEL,
  goods_example,
  fee_display,
  service_example,
  type CommissionMode,
  type Coverage,
  type CoverageSummary,
  type GoodsCoverage,
  type ServiceCoverage,
  type Vertical,
} from '@/features/commission/types';

const TABS = ['products', 'services', 'adoption'] as const;
type Tab = (typeof TABS)[number];

const TAB_COPY: Record<Tab, { label: string; title: string; blurb: string }> = {
  products: {
    label: 'Products',
    title: 'Platform fees on products',
    blurb: 'Added on top: the vendor keeps the price they set and the customer pays more',
  },
  services: {
    label: 'Services',
    title: 'Platform fees on services',
    blurb: 'Deducted: the customer pays the quoted price and the vendor takes home less',
  },
  adoption: {
    label: 'Adoption',
    title: 'The adopter contact-unlock fee',
    blurb: 'Not a vendor commission — a flat charge on a peer-to-peer listing',
  },
};

export function CommissionPage() {
  const [tab_raw, set_tab] = use_url_state('tab', enum_param(TABS));
  const tab: Tab = tab_raw || 'products';
  const copy = TAB_COPY[tab];

  const { data: coverage, isLoading, error, refetch } = use_commission_coverage();
  const { data: adoption } = use_adoption_fee();

  const metrics = useMemo(
    () => build_metrics(coverage?.summary, adoption?.amount_inr),
    [coverage, adoption],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      tabs={
        <PageTabs
          current={tab}
          on_change={(v) => set_tab(v as Tab)}
          /*
           * No counts on these tabs. Every other module badges a tab with "how many rows
           * are waiting", and a number here would be read the same way — but the only
           * number available is "how many of these are priced", which is the opposite of
           * a queue. The tiles say it properly.
           */
          tabs={TABS.map((v) => ({ value: v, label: TAB_COPY[v].label }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      {error ? (
        <ErrorState
          title="Could not load the fees"
          description="Nothing has been changed. Try again."
          action={
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      ) : tab === 'adoption' ? (
        <AdoptionPanel />
      ) : tab === 'products' ? (
        <GoodsTab coverage={coverage} loading={isLoading} />
      ) : (
        <ServicesTab coverage={coverage} loading={isLoading} />
      )}
    </DashboardLayout>
  );
}

/* ------------------------------ the tiles -------------------------------- */

function build_metrics(s: CoverageSummary | undefined, adoption_fee?: string): Metric[] {
  return [
    {
      key: 'priced',
      label: 'Categories priced',
      value: s ? `${s.goods_priced} of ${s.goods_categories}` : undefined,
      hint: 'Charging any fee at all',
      tone: s && s.goods_priced < s.goods_categories ? 'warning' : 'default',
      icon: Boxes,
      loading: !s,
    },
    {
      key: 'uncovered',
      label: 'Earning nothing',
      /*
       * Live listings whose category has no rule and which set no fee of their own. The
       * platform takes zero on every sale of these, and no screen said so before.
       */
      value: s?.listings_uncovered?.toLocaleString('en-IN'),
      hint: s ? `of ${s.listings_total.toLocaleString('en-IN')} live listings` : undefined,
      tone: s && s.listings_uncovered > 0 ? 'destructive' : 'default',
      icon: AlertTriangle,
      loading: !s,
    },
    {
      key: 'overrides',
      label: 'Overriding a rule',
      // Every one of these is a listing or a size a rule change will NOT reach.
      value: s ? (s.product_overrides + s.variant_overrides).toLocaleString('en-IN') : undefined,
      hint: s ? `${s.product_overrides} listings, ${s.variant_overrides} sizes` : undefined,
      to: '/product-approvals?segment=live&has_own_fee=true',
      icon: Percent,
      loading: !s,
    },
    {
      key: 'services',
      label: 'Services priced',
      value: s ? `${s.services_priced} of ${s.service_modules}` : undefined,
      hint: s?.vendors_unpriced ? `${s.vendors_unpriced} vendors charge-free` : 'All modules covered',
      tone: s && s.services_priced < s.service_modules ? 'warning' : 'default',
      icon: Wrench,
      loading: !s,
    },
    {
      key: 'adoption',
      label: 'Adoption unlock',
      value: adoption_fee ? `₹${Number(adoption_fee).toLocaleString('en-IN')}` : undefined,
      hint: 'Paid by the adopter',
      to: '/platform-fees?tab=adoption',
      icon: Heart,
      loading: !adoption_fee,
    },
  ];
}

/* ------------------------------- products -------------------------------- */

function GoodsTab({ coverage, loading }: { coverage: Coverage | undefined; loading: boolean }) {
  const save = use_set_commission_rule();
  /*
   * One open editor at a time. Two half-filled forms on screen is how somebody saves the
   * number they typed for Dry Food onto Treats.
   */
  const [open, set_open] = useState<string | null>(null);

  return (
    <div className="grid gap-gutter">
      {(['FOOD', 'PHARMACY'] as Vertical[]).map((vertical) => {
        const rows = coverage?.goods.filter((g) => g.vertical === vertical) ?? [];
        const priced = rows.filter((r) => r.rule?.is_active).length;
        return (
          <DetailPanel
            key={vertical}
            title={VERTICAL_LABEL[vertical]}
            icon={Boxes}
            description={
              coverage
                ? `${priced} of ${rows.length} categories charge a fee · the fee is added on top of the vendor's price`
                : undefined
            }
            body_class="p-0"
            test_id={`fees-${vertical}`}
          >
            {loading || !coverage ? (
              <div className="p-3.5">
                <PanelSkeleton rows={4} />
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {rows.map((row) => (
                  <GoodsRow
                    // Remount when the saved rule changes, so the inputs reflect it.
                    key={`${row.vertical}-${row.category}-${row.rule?.updated_at ?? 'none'}`}
                    row={row}
                    saving={save.isPending}
                    open={open === `${row.vertical}-${row.category}`}
                    on_open={(next) =>
                      set_open(next ? `${row.vertical}-${row.category}` : null)
                    }
                    on_save={async (mode, value, is_active) => {
                      await save.mutateAsync({
                        vertical: row.vertical,
                        category: row.category,
                        mode,
                        is_active,
                        ...(mode === 'RATE' ? { rate_pct: value } : { amount_inr: value }),
                      });
                    }}
                  />
                ))}
              </ul>
            )}
          </DetailPanel>
        );
      })}
    </div>
  );
}

function GoodsRow({
  row,
  saving,
  open,
  on_open,
  on_save,
}: {
  row: GoodsCoverage;
  saving: boolean;
  open: boolean;
  on_open: (next: boolean) => void;
  on_save: (mode: CommissionMode, value: number, is_active: boolean) => Promise<void>;
}) {
  const testid = `${row.vertical}-${row.category}`;
  return (
    <RuleRow
      testid={testid}
      label={row.label}
      retired={!row.is_active}
      rule={row.rule}
      example={goods_example}
      saving={saving}
      open={open}
      on_open={on_open}
      on_save={on_save}
      has_reach={row.listings > 0}
      reach={
        <>
          {/*
            What the rule is worth. A 10% cut of a category nobody sells in is worth
            nothing, and a rule forty listings override is not the rule it appears to be.
          */}
          <ReachChip
            value={row.listings}
            singular="live listing"
            plural="live listings"
            zero="Nothing on sale here"
            test_id={`reach-listings-${testid}`}
          />
          {row.overrides > 0 && (
            <Badge tone="warning" size="sm" data-testid={`reach-overrides-${testid}`}>
              {row.overrides} override{row.overrides === 1 ? 's' : ''} it
            </Badge>
          )}
          {row.masters > 0 && (
            <Link
              to={`/catalogue?category=${row.category}`}
              className="text-micro text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
              data-testid={`reach-masters-${testid}`}
            >
              {row.masters} in the catalogue
            </Link>
          )}
        </>
      }
    />
  );
}

/* ------------------------------- services -------------------------------- */

function ServicesTab({ coverage, loading }: { coverage: Coverage | undefined; loading: boolean }) {
  const save = use_set_service_commission_rule();
  const [open, set_open] = useState<string | null>(null);
  const rows = coverage?.services ?? [];
  const priced = rows.filter((r) => r.rule?.is_active).length;

  return (
    <DetailPanel
      title="Service modules"
      icon={Wrench}
      description={
        coverage
          ? `${priced} of ${rows.length} modules charge a fee · the fee comes out of what the customer already paid`
          : undefined
      }
      body_class="p-0"
      test_id="fees-services"
    >
      {loading || !coverage ? (
        <div className="p-3.5">
          <PanelSkeleton rows={5} />
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <ServiceRow
              key={`${row.module_type}-${row.rule?.updated_at ?? 'none'}`}
              row={row}
              saving={save.isPending}
              open={open === row.module_type}
              on_open={(next) => set_open(next ? row.module_type : null)}
              on_save={async (mode, value, is_active) => {
                await save.mutateAsync({
                  module_type: row.module_type,
                  mode,
                  is_active,
                  ...(mode === 'RATE' ? { rate_pct: value } : { amount_inr: value }),
                });
              }}
            />
          ))}
        </ul>
      )}
    </DetailPanel>
  );
}

function ServiceRow({
  row,
  saving,
  open,
  on_open,
  on_save,
}: {
  row: ServiceCoverage;
  saving: boolean;
  open: boolean;
  on_open: (next: boolean) => void;
  on_save: (mode: CommissionMode, value: number, is_active: boolean) => Promise<void>;
}) {
  return (
    <RuleRow
      testid={row.module_type}
      label={humanize(row.module_type)}
      rule={row.rule}
      example={service_example}
      saving={saving}
      open={open}
      on_open={on_open}
      on_save={on_save}
      has_reach={row.vendors > 0}
      reach={
        <ReachChip
          value={row.vendors}
          singular="verified vendor"
          plural="verified vendors"
          zero="Nobody offers this yet"
          test_id={`reach-vendors-${row.module_type}`}
        />
      }
    />
  );
}

/* -------------------------------- shared --------------------------------- */

/**
 * How much a rule touches.
 *
 * Zero is called out rather than shown as "0", because "0 live listings" reads as a
 * measurement and "Nothing on sale here" reads as the reason not to bother.
 */
function ReachChip({
  value,
  singular,
  plural,
  zero,
  test_id,
}: {
  value: number;
  singular: string;
  plural: string;
  zero: string;
  test_id: string;
}) {
  return value === 0 ? (
    <span className="text-micro text-muted-foreground" data-testid={test_id}>
      {zero}
    </span>
  ) : (
    <span className="text-micro font-medium text-foreground" data-testid={test_id}>
      {value.toLocaleString('en-IN')} {value === 1 ? singular : plural}
    </span>
  );
}

type AnyRule = {
  mode: CommissionMode;
  rate_pct: number | null;
  amount_inr: number | null;
  is_active: boolean;
  updated_at: string;
} | null;

/**
 * One rule: what it charges, what that means, and what it touches.
 *
 * Read-first, edit on demand. Every row used to carry a live mode select, a value box, a
 * checkbox and a Save button — twenty-one sets of controls down a page that is read far
 * more often than it is written, so the screen was four-fifths furniture and one-fifth
 * information.
 *
 * It was also quietly misleading. An empty input shows a placeholder, and a greyed "10" in
 * the box beside a category charging nothing reads at a glance as "Toys charges 10%" —
 * which is the one sentence a fee screen must never accidentally say. The ticked "Charge
 * it" next to "No fee set" was the same kind of lie: a draft state dressed as the current
 * one. Neither can happen when the controls only exist while you are actually editing.
 */
function RuleRow({
  label,
  /** A retired category still charges on what is already filed under it. */
  retired,
  rule,
  example,
  reach,
  /**
   * Whether an unset rule is actually costing anything.
   *
   * A category with no rule AND nothing on sale in it is not a leak, and rendering eight
   * of those in alarm red teaches an operator to ignore the colour by the time they reach
   * the one that matters.
   */
  has_reach,
  open,
  on_open,
  on_save,
  saving,
  testid,
}: {
  label: string;
  retired?: boolean;
  rule: AnyRule;
  example: (r: { mode: CommissionMode; rate_pct: number | null; amount_inr: number | null }) => string;
  reach: React.ReactNode;
  has_reach: boolean;
  open: boolean;
  on_open: (next: boolean) => void;
  on_save: (mode: CommissionMode, value: number, is_active: boolean) => Promise<void>;
  saving: boolean;
  testid: string;
}) {
  const charging = !!rule && rule.is_active;
  const display = fee_display(rule, has_reach);

  /*
   * The action names what it will do, rather than saying "Edit" over a blank. On the rows
   * that are costing money, the button is the call to action and should read like one.
   */
  const action_label = !rule ? 'Set a fee' : charging ? 'Change' : 'Resume';

  return (
    <li data-testid={`rule-row-${testid}`}>
      <div className="grid items-center gap-x-4 gap-y-2 px-3.5 py-3 lg:grid-cols-[13rem_9rem_1fr_auto]">
        {/* What it is. */}
        <div className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-heading text-data font-bold">{label}</span>
            {retired && (
              <Badge tone="neutral" size="sm" data-testid={`rule-retired-${testid}`}>
                Retired
              </Badge>
            )}
          </span>
          {rule && (
            <span className="text-micro text-muted-foreground" title={date_time(rule.updated_at)}>
              changed {date_short(rule.updated_at)}
            </span>
          )}
        </div>

        {/*
          What it charges — the number, at the size of the thing the page is about. It used
          to be rendered inside a status pill at the same weight as the phrase "Vendors keep
          everything", which made the amount the least legible part of a fee screen.
        */}
        <div className="flex min-w-0 flex-col">
          <span
            className={cn('font-heading text-base font-bold tabular', FEE_TONE_CLASS[display.tone])}
            data-testid={`rule-state-${testid}`}
          >
            {display.amount}
          </span>
          <span className="text-micro text-muted-foreground" data-testid={`rule-note-${testid}`}>
            {display.note}
          </span>
        </div>

        {/* What it is worth, and what it does to ₹500. */}
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">{reach}</span>
          {/* The worked example is what stops "10%" being read in the wrong direction. */}
          {rule && rule.is_active && (
            <span className="text-micro text-muted-foreground" data-testid={`rule-example-${testid}`}>
              {example(rule)}
            </span>
          )}
        </div>

        <Button
          size="sm"
          /*
           * Always quiet. A solid button on every unpriced row put eight identical pink
           * blocks down the services tab, which is the same cry-wolf problem as eight
           * identical red badges — and redundant besides, since the amount column already
           * says "Nothing · vendors keep it all" in red. Severity lives there; this is
           * just the way in, and the label carries the difference.
           */
          variant="outline"
          onClick={() => on_open(!open)}
          data-testid={`rule-edit-${testid}`}
          aria-expanded={open}
        >
          {open ? (
            <>
              <X className="mr-1 h-4 w-4" /> Close
            </>
          ) : (
            <>
              <Pencil className="mr-1 h-4 w-4" /> {action_label}
            </>
          )}
        </Button>
      </div>

      {open && (
        <RuleEditor
          label={label}
          rule={rule}
          example={example}
          saving={saving}
          testid={testid}
          on_done={() => on_open(false)}
          on_save={on_save}
        />
      )}
    </li>
  );
}

/**
 * The controls, only while they are wanted.
 *
 * Nothing here is prefilled from a placeholder: an empty box is empty, and the preview
 * underneath says nothing until there is a real number in it. On a screen where the value
 * is money, a hint that looks like a setting is worse than no hint.
 */
function RuleEditor({
  label,
  rule,
  example,
  saving,
  testid,
  on_done,
  on_save,
}: {
  label: string;
  rule: AnyRule;
  example: (r: { mode: CommissionMode; rate_pct: number | null; amount_inr: number | null }) => string;
  saving: boolean;
  testid: string;
  on_done: () => void;
  on_save: (mode: CommissionMode, value: number, is_active: boolean) => Promise<void>;
}) {
  const [mode, set_mode] = useState<CommissionMode>(rule?.mode ?? 'RATE');
  const [value, set_value] = useState(
    rule ? String(rule.mode === 'FLAT' ? (rule.amount_inr ?? 0) : (rule.rate_pct ?? 0)) : '',
  );
  const [active, set_active] = useState(rule?.is_active ?? true);
  const [error, set_error] = useState<string | null>(null);

  const numeric = Number(value);
  const valid = value !== '' && Number.isFinite(numeric) && numeric >= 0 && (mode === 'FLAT' || numeric <= 100);
  const draft = {
    mode,
    rate_pct: mode === 'RATE' ? numeric : null,
    amount_inr: mode === 'FLAT' ? numeric : null,
  };

  async function save() {
    set_error(null);
    if (value === '' || !Number.isFinite(numeric) || numeric < 0) {
      return set_error(mode === 'RATE' ? 'Enter a percentage.' : 'Enter an amount in rupees.');
    }
    if (mode === 'RATE' && numeric > 100) return set_error('A rate cannot exceed 100%.');
    try {
      await on_save(mode, numeric, active);
      notify.success(
        active
          ? `${label} now charges ${mode === 'RATE' ? `${numeric}%` : `₹${numeric}`}`
          : `${label} paused`,
        {
          description: active
            ? 'Vendors selling in this category are notified.'
            : 'The rule is kept but nothing is charged.',
        },
      );
      on_done();
    } catch (e) {
      set_error(e instanceof Error ? e.message : 'Could not save this rule.');
    }
  }

  return (
    <div
      className="border-t border-border bg-muted/30 px-3.5 py-3"
      data-testid={`rule-editor-${testid}`}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor={`mode-${testid}`}>Fee type</Label>
          <select
            id={`mode-${testid}`}
            className="console-select w-32"
            value={mode}
            onChange={(e) => set_mode(e.target.value as CommissionMode)}
            data-testid={`rule-mode-${testid}`}
          >
            <option value="RATE">Percent</option>
            <option value="FLAT">Flat ₹</option>
          </select>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor={`value-${testid}`}>{mode === 'RATE' ? 'Percent of the price' : 'Rupees per sale'}</Label>
          <div className="relative">
            {mode === 'FLAT' && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-data text-muted-foreground"
              >
                ₹
              </span>
            )}
            <input
              id={`value-${testid}`}
              type="number"
              min={0}
              step="0.01"
              autoFocus
              className={cn(
                'console-field w-28 tabular',
                '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                mode === 'FLAT' ? 'pl-6' : 'pr-7',
              )}
              value={value}
              onChange={(e) => set_value(e.target.value)}
              data-testid={`rule-value-${testid}`}
            />
            {mode === 'RATE' && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-data text-muted-foreground"
              >
                %
              </span>
            )}
          </div>
        </div>

        <label className="flex h-9 items-center gap-2 text-data">
          <input
            type="checkbox"
            className="console-checkbox"
            checked={active}
            onChange={(e) => set_active(e.target.checked)}
            data-testid={`rule-active-${testid}`}
          />
          Charge this fee
        </label>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => void save()} disabled={saving} data-testid={`rule-save-${testid}`}>
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={on_done} disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>

      {/* Silent until there is a real number — a preview of a placeholder is a guess. */}
      <p className="mt-2 text-micro text-muted-foreground" data-testid={`rule-example-${testid}`}>
        {valid
          ? active
            ? example(draft)
            : `${example(draft)} — once you switch "Charge this fee" back on`
          : 'Enter a value to see what a customer would pay'}
      </p>

      {error && (
        <p className="mt-1 text-micro font-medium text-destructive" data-testid={`rule-error-${testid}`}>
          {error}
        </p>
      )}
    </div>
  );
}

/* -------------------------------- adoption -------------------------------- */

/**
 * The adopter contact-unlock fee.
 *
 * Its own tab rather than a card wedged above the others, because it is not a cut of a
 * vendor's price at all: it is a flat charge to an ADOPTER on a peer-to-peer listing, with
 * no vendor and no payout. Filing it beside twenty-one vendor commissions would say
 * something false about whose money it is.
 */
function AdoptionPanel() {
  const fee_q = use_adoption_fee();
  const save = use_set_adoption_fee();
  const [draft, set_draft] = useState('');
  const [error, set_error] = useState<string | null>(null);

  // The server value is the truth until the admin starts typing over it.
  const current = fee_q.data?.amount_inr ?? '';
  const value = draft === '' ? current : draft;
  const changed = draft !== '' && draft !== current;

  async function submit() {
    set_error(null);
    const n = Number(draft);
    if (!Number.isFinite(n) || n < 0) return set_error('Enter a fee of 0 or more.');
    if (Math.round(n * 100) !== n * 100) return set_error('At most 2 decimal places.');
    try {
      await save.mutateAsync(n);
      set_draft('');
      notify.success(`Adoption unlock is now ₹${n}`, {
        description: 'Anyone mid-checkout keeps the amount they were quoted.',
      });
    } catch (e) {
      set_error(e instanceof Error ? e.message : 'Could not change the fee.');
    }
  }

  return (
    <div className="grid items-start gap-gutter lg:grid-cols-[1.2fr_1fr]">
      <DetailPanel
        title="Contact-unlock fee"
        icon={Heart}
        description="What an adopter pays to see a lister’s phone number"
        test_id="adoption-fee-card"
      >
        <div className="grid gap-3">
          <p className="max-w-prose text-data text-muted-foreground">
            This is not a vendor commission. Adoption is peer-to-peer, so nobody is being paid
            out — the whole fee is the platform’s. Changing it applies to new requests; anyone
            mid-checkout keeps the amount they were quoted.
          </p>

          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1.5">
              <Label htmlFor="adoption-fee">Fee in rupees</Label>
              <div className="relative">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-data text-muted-foreground"
                >
                  ₹
                </span>
                <input
                  id="adoption-fee"
                  className="console-field w-36 pl-6 tabular"
                  value={value}
                  onChange={(e) => set_draft(e.target.value)}
                  inputMode="decimal"
                  aria-label="Adoption fee in rupees"
                  data-testid="adoption-fee-input"
                />
              </div>
            </div>
            <Button
              onClick={() => void submit()}
              disabled={!changed || save.isPending}
              data-testid="adoption-fee-save"
            >
              {save.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-1 h-4 w-4" />
              )}
              Save fee
            </Button>
            {changed && (
              <span className="pb-2 text-micro text-muted-foreground">was ₹{current}</span>
            )}
          </div>

          {error && (
            <p className="text-data text-destructive" data-testid="adoption-fee-error">
              {error}
            </p>
          )}
        </div>
      </DetailPanel>

      <DetailPanel title="Where it applies" icon={IndianRupee} test_id="adoption-fee-context">
        {fee_q.isLoading ? (
          <PanelSkeleton rows={3} />
        ) : (
          <FieldList>
            <Field
              label="Charged to"
              value="The adopter, once, to unlock a lister’s number"
            />
            <Field label="Paid out to" value="Nobody — there is no vendor on an adoption" />
            <Field
              label="Last changed"
              value={fee_q.data?.updated_at ? date_time(fee_q.data.updated_at) : 'Never'}
            />
            <Field
              label="Listings"
              value={
                <Link
                  to="/adoption"
                  className="inline-flex items-center gap-1.5 underline-offset-2 hover:text-primary hover:underline"
                >
                  <Store className="h-3.5 w-3.5" /> Open adoption
                </Link>
              }
            />
          </FieldList>
        )}
      </DetailPanel>
    </div>
  );
}
